import { useMemo, useState } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import AttachmentThumbnail from "./AttachmentThumbnail";
import AttachmentPreviewModal from "./AttachmentPreviewModal";
import { authHeaders, formatBytes } from "./attachmentUtils";
import { useUniboxColors } from "../../useUniboxColors";

const API = process.env.REACT_APP_API_URL;

export default function AttachmentPreview({ file, subject = "" }) {
  const c = useUniboxColors();
  const [modalOpen, setModalOpen] = useState(false);

  const modalItem = useMemo(
    () => ({
      id: file.id,
      filename: file.filename,
      mimetype: file.mimetype,
      size: file.size,
      subject,
    }),
    [file, subject]
  );

  const handleDownload = async (event) => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    try {
      const res = await fetch(`${API}/emails/attachment/${file.id}/download`, {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.filename || "adjunto";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      window.alert("No se pudo descargar el adjunto.");
    }
  };

  return (
    <>
      <Box
        onClick={() => setModalOpen(true)}
        sx={{
          borderRadius: "12px",
          border: `1px solid ${c.border}`,
          overflow: "hidden",
          bgcolor: c.card,
          cursor: "pointer",
          transition: "border-color 0.12s, box-shadow 0.12s, transform 0.12s",
          "&:hover": {
            borderColor: c.accent,
            boxShadow: c.shadowSm,
            transform: "translateY(-1px)",
          },
        }}
      >
        <Box
          sx={{
            p: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            bgcolor: c.panel,
            minHeight: 96,
          }}
        >
          <AttachmentThumbnail
            attachmentId={file.id}
            filename={file.filename}
            mimetype={file.mimetype}
            size={80}
          />
        </Box>

        <Box sx={{ px: 1, py: 0.85 }}>
          <Typography
            title={file.filename}
            sx={{
              fontWeight: 700,
              fontSize: 11,
              color: c.textPrimary,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {file.filename}
          </Typography>
          <Box
            sx={{
              mt: 0.35,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 0.5,
            }}
          >
            <Typography sx={{ fontSize: 10, color: c.textDim }}>
              {formatBytes(file.size)}
            </Typography>
            <IconButton
              size="small"
              title="Descargar"
              onClick={handleDownload}
              sx={{
                p: 0.25,
                color: c.textMuted,
                "&:hover": { color: c.accent },
              }}
            >
              <DownloadOutlinedIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Box>
        </Box>
      </Box>

      <AttachmentPreviewModal
        open={modalOpen}
        item={modalItem}
        onClose={() => setModalOpen(false)}
      />
    </>
  );
}
